import { translateBackend } from '@assets/i18n/config';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TabNav } from '@ovComponents/3-pattern/tabNav/tabNav';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { AccordionHoldings } from '@ovComponents/4-module/widgets/holdings/components/accordionHoldings';
import { calculatePrimaryAssetClassHoldings } from '@ovComponents/4-module/widgets/holdings/holdings.resources';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Grid, Skeleton, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const AssignPortfolioToSubAccountVisual = ({
  options,
  objectData,
  loading,
  updateObject,
  continueFunc,
  suggestedFinancialProduct,
  workflowCompletion,
  lockThemeSelection,
  manuallyAssignModelPortfolios,
  showTabs,
  tabsData,
  activeSubAccountIndex,
  setActiveSubAccountIndex,
  householdOwnsModel = false,
}: {
  options: any;
  objectData: any;
  loading: boolean;
  updateObject: {
    handleThemeChange: (themeId: string) => void;
    handleFinancialProductChange: (productId: string) => void;
  };
  continueFunc: any;
  suggestedFinancialProduct?: any;
  workflowCompletion?: any;
  lockThemeSelection?: boolean;
  manuallyAssignModelPortfolios?: boolean;
  showTabs?: boolean;
  tabsData?: any[];
  activeSubAccountIndex?: number;
  setActiveSubAccountIndex?: (index: number) => void;
  householdOwnsModel?: boolean;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'accountTypes', 'components']);
  const [assetClassHoldings, setAssetClassHoldings] = useState<any[]>([]);
  const submit = async () => {
    if (!objectData?.themeId) return;
    sendAnalytic(
      ovAnalyticsEvents.workflowsAssignPortfolioToSubAccountContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );
    await continueFunc();
  };

  useEffect(() => {
    if (!objectData?.financialProduct) {
      setAssetClassHoldings([]);
    }

    if (
      suggestedFinancialProduct &&
      !manuallyAssignModelPortfolios &&
      suggestedFinancialProduct.children
    ) {
      const holdings = calculatePrimaryAssetClassHoldings(
        suggestedFinancialProduct.children.map((x: any) => ({
          ...x,
          totalCents: x.percentage,
        })),
        100,
      );
      setAssetClassHoldings(holdings);
    }

    const financialProductChildren =
      objectData?.financialProduct?.children ??
      objectData?.availableFinancialProducts?.find(
        (financialProduct: any) =>
          financialProduct?.id === objectData?.financialProduct?.id,
      )?.children ??
      [];

    if (
      objectData?.financialProduct &&
      manuallyAssignModelPortfolios &&
      financialProductChildren.length > 0
    ) {
      const holdings = calculatePrimaryAssetClassHoldings(
        financialProductChildren.map((x: any) => ({
          ...x,
          totalCents: x.percentage,
        })),
        100,
      );
      setAssetClassHoldings(holdings);
    }
  }, [
    suggestedFinancialProduct,
    setAssetClassHoldings,
    manuallyAssignModelPortfolios,
    objectData?.financialProduct,
  ]);

  /* pre-select if there's exactly one theme available */
  // Only run once: use a ref to ensure this effect only runs a single time
  const hasPreselectedTheme = useRef(false);
  useEffect(() => {
    if (
      !hasPreselectedTheme.current &&
      objectData?.availableThemes?.length === 1 &&
      !objectData.themeId
    ) {
      hasPreselectedTheme.current = true;
      updateObject.handleThemeChange(objectData.availableThemes[0].id);
    }
  }, [objectData?.availableThemes, objectData?.themeId, updateObject]);

  if (!options) return <></>;

  if (householdOwnsModel) {
    return (
      <WorkflowCompletionForm
        onSubmit={async () => {
          await continueFunc();
        }}
        submitButtonProps={{
          label: t('continue'),
          disabled: loading,
          dataTestId: 'theme-selection-continue-button',
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
        <Typography variant='bodyMedium' sx={{ mt: 2, mb: 3, fontWeight: 500 }}>
          {t('components:householdPortfolio.inheritedFromHousehold')}
        </Typography>
        {loading && (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Skeleton width='100%' height='128px' variant='rounded' />
            </Grid>
          </Grid>
        )}
      </WorkflowCompletionForm>
    );
  }

  const selectedFinancialProduct =
    objectData?.availableFinancialProducts?.find(
      (fp: any) => fp.id === objectData?.financialProduct?.id,
    ) ?? suggestedFinancialProduct;

  const selectedProductUrl = translateBackend(
    selectedFinancialProduct?.translatedUrl,
  );

  const showUrlModelPortfolioUrl =
    options?.displayModelPortfolioUrl && selectedProductUrl ? (
      <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
        {`[${t('components:portfolioFundFacts')}](${selectedProductUrl})`}
      </ReactMarkdown>
    ) : (
      <></>
    );

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || !objectData.themeId,
        dataTestId: 'theme-selection-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {showTabs && tabsData && tabsData.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <TabNav
            tabs={tabsData}
            activeTab={activeSubAccountIndex || 0}
            setActiveTab={setActiveSubAccountIndex || (() => {})}
          />
        </Box>
      )}
      {loading && (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Skeleton width='100%' height='128px' variant='rounded' />
          </Grid>
          <Grid size={12}>
            <Skeleton width='100%' height='300px' variant='rounded' />
          </Grid>
        </Grid>
      )}
      {objectData?.availableThemes?.length > 0 && !loading && (
        <>
          <SelectField
            testId='theme-selection'
            disabled={lockThemeSelection || loading}
            onChange={(e: any) =>
              updateObject.handleThemeChange(e.target.value)
            }
            value={objectData.themeId || ''}
            label={translateBackend(options?.themeLabel)}
            infoTooltip={additionalInfo(options?.themeLabel?.additionalInfo)}
            fullWidth
          >
            {objectData.availableThemes?.map((theme: any) => (
              <MenuItem
                data-testid={`theme-selection-${kebabCase(translateBackend(theme.translatedName))}`}
                key={theme.id}
                value={theme.id}
              >
                {translateBackend(theme.translatedName)}
              </MenuItem>
            ))}
          </SelectField>
          <Typography variant='bodyLarge' sx={{ mt: 2, mb: 3 }}>
            {translateBackend(
              objectData.availableThemes?.find(
                (theme: any) => theme.id === objectData.themeId,
              )?.translatedDescription,
            )}
          </Typography>
          {manuallyAssignModelPortfolios && (
            <SelectField
              key={objectData.financialProduct?.id}
              testId='model-selection'
              disabled={loading}
              onChange={(e: any) =>
                updateObject.handleFinancialProductChange(e.target.value)
              }
              fullWidth
              value={objectData.financialProduct?.id || ''}
              label={translateBackend(options?.modelPortfolioLabel)}
              infoTooltip={additionalInfo(
                options?.modelPortfolioLabel?.additionalInfo,
              )}
            >
              {objectData.availableFinancialProducts?.map((fp: any) => (
                <MenuItem
                  data-testid={`model-portfolio-${kebabCase(translateBackend(fp.translatedName))}`}
                  key={fp.id}
                  value={fp.id}
                >
                  {translateBackend(fp.translatedName)}{' '}
                  {!!objectData.userSuitability &&
                    suggestedFinancialProduct?.id === fp.id &&
                    ` (${t('components:changeTheme.suggested')})`}
                </MenuItem>
              ))}
            </SelectField>
          )}
          {showUrlModelPortfolioUrl}
          <AccordionHoldings
            data-testid='accordion-holding'
            assetClassHoldings={assetClassHoldings}
            loading={loading}
            variant='outlined'
          />
        </>
      )}
    </WorkflowCompletionForm>
  );
};

export default AssignPortfolioToSubAccountVisual;
